import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import { useApi } from "@/api/hooks/useApi";
import type { EventDto, ImageDto } from "@/api/types";
import { ToggleButton } from "@/components/Button/ToggleButton";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { formatDate, FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Label } from "@/components/Label/Label";
import { UserAvatarList } from "@/components/UserAvatarList/UserAvatarList";
import { isDefined } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { canManageEvents } from "@/modules/events/permissions";
import { getEventCategoryName } from "@/modules/events/util";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface EventCardProps {
  event: EventDto;
  isPast?: boolean;
}
export function EventCard({ event, isPast }: EventCardProps): React.ReactNode {
  const projectId = useProjectId();
  const slug = useSlug();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const hasPermission = usePermission();
  const showFlashToast = useFlashToast();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("delete-event-modal");
  const { mutateAsync: deleteEvent } = useMutation({
    mutationFn: (payload: { eventId: string }) => api.deleteEventsByIdV1(payload.eventId).then(({ data }) => data),
    onSuccess: () => {
      showFlashToast({
        type: "success",
        title: t("model.event.action.delete.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("model.event.action.delete.notification.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId),
      });
    },
  });
  const { mutate: signUp, isPending: isSigningUp } = useMutation({
    mutationFn: (id: string) => api.postEventsSignupV1(id),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId),
      });
    },
  });
  const { mutate: signOff, isPending: isSigningOff } = useMutation({
    mutationFn: (id: string) => api.postEventsSignoffV1(id),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId),
      });
    },
  });

  const isLoading = isSigningUp || isSigningOff;
  function getSpotsLabel(): string | null {
    const participantsExist = isDefined(event.maxParticipants) && isDefined(event.participantCount);
    const isFull = participantsExist ? event.participantCount! >= event.maxParticipants! : false;
    const restSpotsAmount = participantsExist && !isFull ? event.maxParticipants! - event.participantCount! : null;
    const noLimit = event.maxParticipants == null;
    if (event.isSignedUp) {
      return t("page.calendar.events.spots.is-signed-up");
    }
    if (isFull) {
      return t("page.calendar.events.spots.no-spots");
    }
    if (!event.canParticipate) {
      return null;
    }
    if (isDefined(restSpotsAmount)) {
      return t("page.calendar.events.spots.free-spots", {
        count: event.maxParticipants! - event.participantCount!,
      });
    }
    if (noLimit) {
      return t("page.calendar.events.spots.is-not-signed-up");
    }

    return null;
  }
  const handleToggleParticipation = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (event.isSignedUp) signOff(event.id);
    else signUp(event.id);
  };

  const handleOpenEvent = () => {
    posthog?.capture("clicked_event");
    void navigate(
      routes.events.details({
        slug,
        id: event.id,
      }),
    );
  };

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      icon: <Edit05Icon />,
      value: "edit",
      callbackFn: () => {
        if (parseISO(event.startsAt) < new Date()) {
          showFlashToast({
            type: "info",
            title: t("page.calendar.events.edit.notification.is-started"),
          });

          return;
        }
        void navigate(
          routes.events.edit({
            slug,
            id: event.id,
          }),
        );
      },
      isHidden: !event.canEdit,
    },
    {
      labels: {
        default: t("common.action.duplicate"),
      },
      icon: <Copy01Icon />,
      value: "duplicate",
      callbackFn: () => {
        void navigate(
          routes.events.create({
            slug,
          }),
          {
            state: {
              id: event.id,
            },
          },
        );
      },
      isHidden: !hasPermission(canManageEvents),
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      icon: <Trash02Icon />,
      value: "delete",
      callbackFn: () => openDeleteModal(event.id),
      isHidden: !event.canDelete,
    },
  ];

  return (
    <>
      <div
        data-testid="event-card"
        className="relative cursor-pointer rounded-lg border border-grey-100 bg-white shadow-sm hover:shadow-lg"
        onClick={handleOpenEvent}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleOpenEvent();
          }
        }}
        role="link"
      >
        <div className="flex h-full items-center gap-4 px-4 py-2.5">
          <div className="flex h-full flex-col items-center justify-center gap-1 rounded-lg border border-grey-100 px-2 py-1">
            <span className="text-caption text-grey-700">
              {formatDate(i18n, "weekDayShort", parseISO(event.startsAt))}
            </span>
            <span className={twJoin("text-headline3 leading-old-headline4", isPast ? "text-grey-700" : undefined)}>
              {parseISO(event.startsAt).getDate()}
            </span>
            <span className="text-body text-grey-700">{formatDate(i18n, "monthShort", parseISO(event.startsAt))}</span>
          </div>
          <div className="flex w-full flex-col gap-1">
            <div className="flex w-full justify-between">
              <div className="flex flex-wrap gap-2">
                {!event.publishedAt && (
                  <Label theme="blue" center={false}>
                    <div className="flex gap-1 whitespace-nowrap">
                      {t("page.calendar.events.scheduled")}
                      <FormattedDate date={event.publishAt} format="datetimeShort" />
                    </div>
                  </Label>
                )}
                {event.category && (
                  <Label theme="gray" center={false}>
                    {getEventCategoryName(t, event.category.id)}
                  </Label>
                )}
              </div>
              <ContextMenu items={menuActions} />
            </div>
            <span className={twJoin("text-body-bold", isPast && "text-grey-700")}>{event.name}</span>
            <span className="flex flex-wrap items-center gap-1 text-grey-700">
              <span className="whitespace-nowrap text-caption">{`${format(parseISO(event.startsAt), "HH:mm")} - ${format(parseISO(event.endsAt), "HH:mm")}`}</span>
              {event.location && <span className="text-caption">• {event.location}</span>}
              {event.updatedAt && (
                <span className="whitespace-nowrap text-caption-bold">• {t("page.calendar.events.edited")}</span>
              )}
            </span>
            {!isPast && <span className="text-caption text-grey-700">{getSpotsLabel()}</span>}
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <UserAvatarList
                images={event.participants.filter((x) => x.avatar !== undefined).map((x) => x.avatar as ImageDto)}
                limit={3}
                totalElements={event.participantCount}
              />
              {!isPast && event.canParticipate && (
                <ToggleButton
                  config={{
                    checked: {
                      label: t("page.calendar.events.leave"),
                      icon: <CheckIcon />,
                    },
                    unchecked: {
                      label: t("page.calendar.events.join"),
                      icon: <PlusIcon />,
                    },
                  }}
                  size="sm"
                  onClick={handleToggleParticipation}
                  isChecked={event.isSignedUp}
                  {...{
                    isLoading,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <DeleteModal
        title={t("page.calendar.events.delete.modal.title")}
        description={t("page.calendar.events.delete.modal.description")}
        onDelete={(eventId) =>
          deleteEvent({
            eventId,
          })
        }
        deleteBtnProps={{
          "data-testid": "delete-event-modal",
        }}
        {...deleteModalProps}
      />
    </>
  );
}
